import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, Zap, Users, Rocket, ArrowRight, Instagram, Facebook, Music2, Youtube, MessageCircle, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Defaults en caso de que el super master aún no haya editado el contenido.
// Estos valores mantienen la landing FStudy Manager que ya existía.
const DEFAULTS = {
  hero: {
    title: "FStudy Manager",
    subtitle: "Create ART, Design & Marketing — la plataforma unificada para gestionar tu marca, redes, contenido y equipo con IA.",
    background_url: "/assets/hero-bg.png",
    cta_text: "Iniciar sesión",
    cta_url: "/login",
  },
  what_is: {
    heading: "¿Qué es FStudy Manager?",
    subheading: "Un ecosistema empresarial multi-empresa para agencias y equipos de marketing. Diseño IA, publicación multi-red, CRM con score y aprobaciones por cliente.",
    items: [],
  },
  modules: {
    heading: "Módulos incluidos",
    subheading: "Todo lo que necesitas, aislado por empresa y controlado por rol.",
    items: [
      { title: "Motor Creativo", description: "Generación de piezas con IA (Nano Banana), edición image-to-image, referencias visuales y nota de voz.", icon: "sparkles" },
      { title: "Redes Sociales", description: "Instagram (Feed/Reels/Stories/Carrusel), Facebook, TikTok y YouTube desde un solo composer.", icon: "instagram" },
      { title: "WhatsApp Cloud", description: "Envío de mensajes, plantillas y automatizaciones vía WhatsApp Business Cloud API.", icon: "message" },
      { title: "Email Marketing", description: "Campañas segmentadas por tags, listas y CRM con score de interacción.", icon: "mail" },
      { title: "Agenda / CRM", description: "Contactos multicanal con auto-etiquetado por fuente y temperatura (frío/tibio/caliente).", icon: "users" },
      { title: "Aprobaciones", description: "Flujo configurable por empresa. Master aprueba o auto-aprueba según preferencia.", icon: "rocket" },
    ],
  },
  how_it_works: {
    heading: "Cómo funciona",
    subheading: "En tres pasos.",
    items: [
      { title: "1 · Conecta", description: "Vincula IG, FB, YouTube, WhatsApp y Gmail por empresa vía OAuth gestionado (Composio)." },
      { title: "2 · Crea", description: "Genera piezas con IA, edítalas, aprueba y programa la publicación multi-red." },
      { title: "3 · Analiza", description: "Métricas en tiempo real por publicación, canal y empresa; alertas por umbral." },
    ],
  },
  cta_secondary: {
    title: "¿Listo para trabajar en escala?",
    subtitle: "Solicita acceso a tu Master de empresa o contacta al equipo FStudy.",
    cta_text: "Iniciar sesión",
    cta_url: "/login",
  },
  footer: {
    tagline: "FStudy Manager · Create ART, Design & Marketing",
    contact_email: "hola@7-study.com",
    contact_phone: "",
    social_links: {},
  },
};

function IconFor({ name, className }) {
  const map = {
    sparkles: Sparkles, instagram: Instagram, facebook: Facebook, tiktok: Music2,
    youtube: Youtube, message: MessageCircle, mail: Mail, users: Users, rocket: Rocket,
    zap: Zap,
  };
  const C = map[(name || "").toLowerCase()] || Sparkles;
  return <C className={className} strokeWidth={1.7} />;
}

function mergeDeep(base, overrides) {
  if (!overrides) return base;
  const out = { ...base };
  for (const key of Object.keys(overrides)) {
    const v = overrides[key];
    if (v == null || v === "") continue;
    if (Array.isArray(v)) {
      if (v.length > 0) out[key] = v;
    } else if (typeof v === "object") {
      out[key] = mergeDeep(base?.[key] || {}, v);
    } else {
      out[key] = v;
    }
  }
  return out;
}

export default function LandingPublic() {
  const [config, setConfig] = useState(DEFAULTS);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/landing/public`)
      .then((r) => { if (alive && r.data) setConfig(mergeDeep(DEFAULTS, r.data)); })
      .catch(() => { /* silencio: usar defaults */ });
    // Fase 29 · cargar planes públicos publicados
    axios.get(`${BACKEND_URL}/api/billing/plans/public`)
      .then((r) => { if (alive && Array.isArray(r.data)) setPlans(r.data); })
      .catch(() => { /* silencio */ });
    return () => { alive = false; };
  }, []);

  const hero = config.hero || DEFAULTS.hero;
  const whatIs = config.what_is || DEFAULTS.what_is;
  const modules = config.modules || DEFAULTS.modules;
  const how = config.how_it_works || DEFAULTS.how_it_works;
  const testimonials = config.testimonials;
  const ctaSecondary = config.cta_secondary || DEFAULTS.cta_secondary;
  const footer = config.footer || DEFAULTS.footer;

  return (
    <div className="min-h-screen bg-white text-brand-ink" data-testid="landing-public">
      {/* Nav */}
      <nav className="border-b border-stone-200 bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="FStudy" className="h-8 w-8" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <span className="font-serif-display text-lg text-brand-ink font-semibold tracking-tight">FStudy Manager</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-brand-emerald-dark hover:underline font-semibold" data-testid="landing-nav-login">Iniciar sesión</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #E9F5EE 0%, #FFFDF5 100%)" }} data-testid="landing-hero">
        {hero.background_url && (
          <img src={hero.background_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-16 pb-20 md:pt-24 md:pb-28 text-center">
          <div className="inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold text-brand-gold-dark bg-brand-gold-soft border border-brand-gold/30 mb-4">
            Empresarial · Multi-tenant · IA
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-brand-ink font-semibold leading-tight tracking-tight max-w-3xl mx-auto">
            {hero.title}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            {hero.subtitle}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={hero.cta_url || "/login"}
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-brand-emerald text-white font-semibold hover:bg-brand-emerald-dark transition shadow-lg"
              data-testid="landing-hero-cta"
            >
              {hero.cta_text || "Iniciar sesión"} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Qué es */}
      {whatIs?.heading && (
        <section className="py-16 sm:py-20 px-4 sm:px-6" data-testid="landing-what-is">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">{whatIs.heading}</h2>
            {whatIs.subheading && <p className="mt-4 text-base sm:text-lg text-stone-600 leading-relaxed">{whatIs.subheading}</p>}
          </div>
        </section>
      )}

      {/* Módulos */}
      {modules?.items?.length > 0 && (
        <section className="py-16 sm:py-20 px-4 sm:px-6 bg-brand-emerald-soft/30" data-testid="landing-modules">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">{modules.heading}</h2>
              {modules.subheading && <p className="mt-3 text-base text-stone-600">{modules.subheading}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {modules.items.map((it, i) => (
                <div key={i} className="rounded-2xl border border-stone-200 bg-white p-6 hover:shadow-lg transition" data-testid={`landing-module-${i}`}>
                  <div className="h-10 w-10 rounded-lg bg-brand-emerald-soft flex items-center justify-center text-brand-emerald-dark mb-3">
                    <IconFor name={it.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-brand-ink mb-1">{it.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{it.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cómo funciona */}
      {how?.items?.length > 0 && (
        <section className="py-16 sm:py-20 px-4 sm:px-6" data-testid="landing-how">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">{how.heading}</h2>
              {how.subheading && <p className="mt-3 text-base text-stone-600">{how.subheading}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {how.items.map((it, i) => (
                <div key={i} className="rounded-2xl border border-brand-gold/30 bg-brand-gold-soft/20 p-6 relative">
                  <h3 className="font-serif-display text-xl text-brand-ink font-semibold mb-2">{it.title}</h3>
                  <p className="text-sm text-stone-700 leading-relaxed">{it.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonios */}
      {testimonials?.items?.length > 0 && (
        <section className="py-16 sm:py-20 px-4 sm:px-6 bg-stone-50" data-testid="landing-testimonials">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">{testimonials.heading || "Testimonios"}</h2>
              {testimonials.subheading && <p className="mt-3 text-base text-stone-600">{testimonials.subheading}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {testimonials.items.map((it, i) => (
                <blockquote key={i} className="rounded-2xl border border-stone-200 bg-white p-6">
                  <p className="italic text-stone-700 leading-relaxed">"{it.description}"</p>
                  {it.title && <footer className="mt-3 text-sm font-semibold text-brand-emerald-dark">— {it.title}</footer>}
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Fase 29 · Planes / Paquetes */}
      {plans.length > 0 && (
        <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white" data-testid="landing-plans">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 sm:mb-14">
              <h2 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">
                Planes y paquetes
              </h2>
              <p className="mt-3 text-base text-stone-600">
                Elige el paquete que mejor se ajuste a tu equipo. Precios en USD.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {plans.map((p) => (
                <div key={p.id} className="rounded-2xl border border-brand-emerald/20 bg-brand-emerald-soft/20 p-6 flex flex-col hover:shadow-xl hover:border-brand-gold transition"
                     data-testid={`landing-plan-${p.id}`}>
                  <div className="font-serif-display text-2xl text-brand-ink font-semibold">{p.name}</div>
                  <div className="text-4xl font-bold text-brand-emerald mt-2">
                    USD {Number(p.price_usd).toFixed(0)}
                  </div>
                  {p.description && <p className="text-sm text-stone-600 mt-3 mb-4">{p.description}</p>}
                  <ul className="space-y-2 text-sm text-brand-ink flex-1">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="h-2 w-2 rounded-full bg-brand-gold mt-1.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/login"
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 bg-brand-gold text-brand-ink font-semibold hover:bg-brand-gold-dark hover:text-white transition"
                  >
                    Contratar <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA secundario */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-brand-ink text-white text-center" data-testid="landing-cta">
        <div className="max-w-3xl mx-auto">
          {ctaSecondary.title && <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold">{ctaSecondary.title}</h2>}
          {ctaSecondary.subtitle && <p className="mt-4 text-base sm:text-lg text-stone-300">{ctaSecondary.subtitle}</p>}
          <Link
            to={ctaSecondary.cta_url || "/login"}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-brand-gold text-brand-ink font-semibold hover:bg-brand-gold-dark hover:text-white transition"
            data-testid="landing-cta-secondary"
          >
            {ctaSecondary.cta_text || "Iniciar sesión"} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 sm:px-6 border-t border-stone-200 text-center text-sm text-stone-500" data-testid="landing-footer">
        <p>{footer.tagline || DEFAULTS.footer.tagline}</p>
        <div className="mt-2 flex flex-wrap gap-4 justify-center">
          {footer.contact_email && <a href={`mailto:${footer.contact_email}`} className="hover:text-brand-emerald-dark">{footer.contact_email}</a>}
          {footer.contact_phone && <span>{footer.contact_phone}</span>}
        </div>
        <p className="mt-4 text-[10px] uppercase tracking-widest text-stone-400">© {new Date().getFullYear()} FStudy Manager</p>
      </footer>
    </div>
  );
}
